export interface DemoOption {
  id: string;
  inputContent: string;
  scenario: string;
  target: string;
  tone: string;
  description: string;
  // Added optional fields for tab preview usage
  shortTitle?: string; // 简短标签标题
  previewImage?: string; // 预览图（相对 public/ 路径）
  sampleOutput?: string; // 预览中展示的示例输出内容（非真实生成）
}

export const demoOptions: DemoOption[] = [
  {
    id: "sales-opener",
    inputContent: "What services does your company provide?",
    scenario: "Business",
    target: "Client",
    tone: "Formal",
  description: `Target company: XXX Manufacturing (Chicago, B2B automation equipment, ~500 employees)
Contact: Ms. Wang, Operations Director
Public info: Their WeChat article mentioned "low follow-up efficiency and poor email reply rates"
Our product: Communicate2Win (auto-generates openers/follow-ups; serving 300+ sales teams)
Goal: Spark interest and start a conversation.
Generate 3 optional Chinese cold openers, each ≤50 Chinese characters:
- Lead with a pain-based question; avoid bragging or hard selling
- Tone: professional, friendly, concise
- Styles: data-driven / empathy-driven / action-driven`
  ,shortTitle: "Sales opener"
  ,previewImage: "/demo-previews/sales-opener.svg"
  ,sampleOutput: `Data-driven: Noticed repeated mentions of low "follow-up efficiency"; we help teams cut manual drafting. Open to a 5‑min look?\nEmpathy: Saw your post on low reply rates—would a tiny opener test next week be useful?\nAction: 3 quick ideas to lift first-touch replies ≥15%; share 2 mins?`
  },
  {
    id: "price-objection",
    inputContent: "Your annual price is $49,000, about 20% higher than Vendor B. Our budget is tight this year; if you can't go lower, we may choose B.",
    scenario: "Message",
    target: "Client",
    tone: "Polite",
    description: `Client priorities: data security, speed of go-live. They saw the demo and like the features.
Our acceptable concessions: tier by seats, monthly billing; do not cut core functionality.
Produce 3 response approaches (value anchoring / tiered plan / trade-offs without discounting), 40–60 English words each:
- Start with acknowledgement and empathy, then anchor on outcomes or TCO
- Offer two alternative plans (e.g., by seats / by month) without weakening core value
- End with one concrete next question (budget range or decision timeline)
- Tone: calm, respectful, open to discuss; avoid "price war" language`
  ,shortTitle: "Price probe"
  ,previewImage: "/demo-previews/price-objection.svg"
  ,sampleOutput: `Value anchor: Teams live ≥90 days report +18–25% reply lift & ~30% rep time saved → total cost gap closes.`
  },
  {
    id: "interview-intro",
    inputContent: `Role: SaaS Sales Manager (XXX Software)
Requirements: build the sales playbook, lead an 8-person team, improve win rate and forecast accuracy
Candidate highlights:
- 5 years B2B sales; team lead in the last 2 years
- Last year's bookings ≥ $12M; ACV $200k–$600k
- Complex, multi-threaded deals; fluent with MEDDICC
- Management: weekly cadence reviews, opportunity grading, post-close handover rhythm`,
    scenario: "Interview",
    target: "Boss",
    tone: "Confident",
    description: `Generate a "30-45 second intro":
- Structure: who I am → 2–3 role-relevant achievements → method/management style → value I bring
- Conversational, speakable; ≤100 words
- Close with a quantifiable 90-day goal`
  ,shortTitle: "Interview intro"
  ,previewImage: "/demo-previews/interview-intro.svg"
  ,sampleOutput: `Hi, I'm Alex. Led an 8-person B2B SaaS team; last year $12M+ bookings, complex multi-thread deals using MEDDICC. I systemize cadence + grading to raise forecast accuracy. In 90 days: implement opportunity scorecard & lift win rate +5pp.`
  },
  {
    id: "email-reply",
    inputContent: `From: Li Ming <li.ming@xxx---.com>Subject: Urgent Notice – Acrylic Resin Stock-out & Delivery Delay

Dear Leo,

Due to an unexpected shutdown at our upstream plant, our inventory of Acrylic Resin AR-128 is depleted. The 3-ton shipment scheduled for 18 August will be delayed by approximately 3–4 weeks. We are actively working on alternatives but cannot guarantee full supply in the short term.

Options:
1) Deliver the full 3 tons in mid-September (no expedite fee).
2) Partial delivery: 1 ton by end-August, remaining 2 tons late-September; air-freight surcharge ¥1,200/ton.
3) Substitute with compatible grade AR-130 (slightly higher specs, +8% price), available 25 August in full.

Please advise your preferred option or other suggestions by this Friday.

Apologies for the inconvenience and thanks for your understanding.

Best regards,
Li Ming
Sales Manager, XYZ Materials`,
    scenario: "Email",
    target: "Supplier",
    tone: "Formal",
    description: `Draft a concise business-English reply email:
- My name is Leo Smith, Purchasing Director at ABC Corp.
- Start with appreciation and acknowledgment
- Briefly state the impact on our production schedule
- Ask clarifying questions on each option: spec equivalency/certification, surcharge details, supply stability
- Propose a hybrid solution: partial delivery + substitute grade, request firm latest arrival dates
- Tone: practical, collaborative, firm on timeline`
  ,shortTitle: "Email reply"
  ,previewImage: "/demo-previews/email-reply.svg"
  ,sampleOutput: `Subject: Re: Urgent Notice – Acrylic Resin Stock-out & Delivery Delay  

Dear Li Ming,  

Thank you for your prompt notification and transparency regarding the delay. `
  },
  {
    id: "follow-up",
    inputContent: "(no reply)",
    scenario: "Email",
    target: "Client",
    tone: "Polite",
    description: `Context: Last Friday the client requested case studies and a quote. I sent them by email; the files were viewed the same day, but there's been no reply (4 days).
Contact: Director Liang (Manufacturing Digitalization Lead)
Goal: Polite nudge, offer two concrete next steps; if not moving forward, provide a graceful opt-out.
My last line previously:"If it works, we can schedule 15 minutes next week to discuss rollout details."
Generate 3 follow-up scripts for email, each ≤30 words:
- Structure: light reminder → re-ignite value (one point) → two specific time windows → graceful opt-out
- Avoid pressure or blame; encourage "quick reply with a number/option"
- Tone: considerate, pragmatic, easy to advance or pause`
  ,shortTitle: "Follow-up"
  ,previewImage: "/demo-previews/follow-up.svg"
  ,sampleOutput: `Hi Director Liang, 
  circling back on the case studies and quote—hope they aligned with your needs. If helpful, I’m available [Tuesday 2-3 PM] or [Thursday 10-11 AM] to discuss rollout details. 
  Alternatively, just reply with ‘1’ or ‘2’ to confirm. If timing isn’t right now, no worries—we can reconnect later.`
  },
  {
    id: "elevator-pitch",
    inputContent: "(Listening in...)",
    scenario: "Business",
    target: "Client",
    tone: "Confident",
    description: `Product: Communicate2Win (conversational scripts & email assistant).  Audience: B2B sales and customer success teams
Pain: writing takes too long, low reply rates, scripts are inconsistent across the team
Differentiators: industry templates + scenario simulator, CRM integration, team best-practice reuse
Impact: writing time ↓70% on average; email reply rate ↑28%; 3,000+ users served
Goal: Deliver a 30-second hallway/elevator pitch that wins a short demo
Generate a "30-second" elevator pitch:
- Structure: one-line positioning → problem → solution → quantified impact / social proof → clear CTA
- Conversational with good cadence; optimized for spoken delivery
- CTA example: "Let me give you a 10-minute demo this week—would Wednesday or Thursday work?"`
  ,shortTitle: "Elevator pitch"
  ,previewImage: "/demo-previews/elevator-pitch.svg"
  ,sampleOutput: `Imagine cutting 70% of the time your team spends writing emails while boosting reply rates by 28%. With **Communicate2Win**, we arm sales and CS teams with industry-tuned scripts, CRM-integrated templates, and a scenario simulator to ensure consistency and speed. `
  }
];